import { FC, Fragment, useMemo } from 'react';
import { OpmForecastDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { selectAllInstrumentsIncludingInitialValues } from '@app/core/store/capital-structure-slice-selectors';
import { sumBy } from '@app/shared/helpers';
import classNames from 'classnames';
import styles from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart.module.scss';
import { createStructureForInstruments, getStructureForForecastStack } from './utilities';
import RankedInstrumentBlock from './RankedInstrumentBlock';
import ChartColumn from './ChartColumn';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import { transformOpmForecastTooltipData } from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-transformers';
import { PerYearCapitalStructureForecastTooltip } from '@app/modules/projects/dashboard/shared/per-year-capital-structure/PerYearCapitalStructureForecastTooltip';

interface InstrumentBlocksByYearProps {
  calculatedLabelTopValue: number;
  totalInvestments: number;
  totalEquityPerForecast: number[];
  chartForecastsCount: number;
  forecastData: OpmForecastDto[];
  onStackClick: (isEntry: boolean, forecastIndex?: number) => void;
}

const PARENT_CLASSNAME = 'total-equity-chart';

const InstrumentBlocksByYear: FC<InstrumentBlocksByYearProps> = ({
  calculatedLabelTopValue,
  totalInvestments,
  totalEquityPerForecast,
  chartForecastsCount,
  forecastData,
  onStackClick,
}) => {
  const allInstruments = useAppSelector(selectAllInstrumentsIncludingInitialValues);

  const entryStackStructure = useMemo(
    () =>
      createStructureForInstruments(
        allInstruments.map((ins) => ({
          ...ins,
          amount: ins.initialValues?.ownership
            ? sumBy(Object.values(ins.initialValues.ownership), (c) => c.amount ?? 0)
            : 0,
        }))
      ),
    [allInstruments]
  );

  const stacksPerForecast = useMemo(
    () =>
      forecastData.map((f) =>
        getStructureForForecastStack(f.forecastDate, forecastData, allInstruments)
      ),
    [forecastData, allInstruments]
  );

  const calculateHeight = (value: number) => `${(100 / calculatedLabelTopValue) * value}%`;

  const entryTooltipData = transformOpmForecastTooltipData(forecastData, undefined);

  return (
    <div className={styles[`${PARENT_CLASSNAME}__chart-inner`]}>
      {entryTooltipData ? (
        <Tooltip
          placement={TooltipPlacement.Right}
          useFixedPositioning
          className={classNames(
            styles[`${PARENT_CLASSNAME}__column`],
            styles[`${PARENT_CLASSNAME}__column--expanded`],
            styles['cursor-pointer']
          )}
          style={{ height: calculateHeight(totalInvestments) }}
          content={
            <PerYearCapitalStructureForecastTooltip
              forecastData={entryTooltipData}
              isEntryTooltip
            />
          }>
          <div style={{ height: '100%' }} onClick={() => onStackClick(true, -1)}>
            <RankedInstrumentBlock
              structure={entryStackStructure}
              chartForecastsCount={chartForecastsCount}
              forecastData={forecastData}
              forecastIndex={undefined}
            />
          </div>
        </Tooltip>
      ) : (
        <ChartColumn
          height={calculateHeight(totalInvestments)}
          onClick={() => onStackClick(true, -1)}>
          <RankedInstrumentBlock
            structure={entryStackStructure}
            chartForecastsCount={chartForecastsCount}
            forecastData={forecastData}
            forecastIndex={undefined}
          />
        </ChartColumn>
      )}

      {Array.from({ length: chartForecastsCount }).map((_, i) => {
        const isFirstForecast = i === 0;
        const isLastForecast = chartForecastsCount - 1 === i;
        const tooltipPlacement = isFirstForecast
          ? TooltipPlacement.Right
          : isLastForecast
          ? TooltipPlacement.Left
          : TooltipPlacement.Top;
        const forecastTooltipData = transformOpmForecastTooltipData(forecastData, i);
        return (
          <Fragment key={`instrument-wrapper-${i}`}>
            <Tooltip
              placement={tooltipPlacement}
              useFixedPositioning
              className={classNames(
                styles[`${PARENT_CLASSNAME}__column`],
                styles[`${PARENT_CLASSNAME}__column--expanded`],
                styles['cursor-pointer']
              )}
              style={{ height: calculateHeight(totalEquityPerForecast[i]) }}
              content={
                <PerYearCapitalStructureForecastTooltip
                  forecastData={forecastTooltipData}
                  isEntryTooltip={false}
                />
              }>
              <div style={{ height: '100%' }} onClick={() => onStackClick(false, i)}>
                <RankedInstrumentBlock
                  structure={stacksPerForecast[i]}
                  chartForecastsCount={chartForecastsCount}
                  forecastData={forecastData}
                  forecastIndex={i}
                />
              </div>
            </Tooltip>
          </Fragment>
        );
      })}
    </div>
  );
};

export default InstrumentBlocksByYear;
